import { Badge, Box, Group, Text, ThemeIcon } from "@mantine/core"
import { CalendarDotsIcon, TruckIcon, UserIcon } from "@phosphor-icons/react"
import { ReactNode, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { authDeliveryApprove, IAuth } from "../../store/Auth"
import { dateTimeSecFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import { PageLayout } from "../layout"
import { ListState } from "../ui"
import SupplierDeliveryDistributingTable from "./table"
import classes from "./form.module.css"

function InfoCell({ icon, label, value, dark }: Readonly<{ icon: ReactNode; label: string; value: ReactNode; dark?: boolean }>) {
  return (
    <Group gap="md" wrap="nowrap" align="center">
      <ThemeIcon size={44} radius="md" variant={dark ? "filled" : "light"} color={dark ? "dark" : "gray"}>
        {icon}
      </ThemeIcon>
      <Box miw={0}>
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
          {label}
        </Text>
        <Text fw={650} fz={dark ? "var(--nw-fz-lead)" : "var(--nw-fz-body)"} truncate>
          {value || "-"}
        </Text>
      </Box>
    </Group>
  )
}

/**
 * «Ачилт баталгаажуулах» (canvas Ord_DeliveryApprove). Reached from the Хүлээлтэнд selection bar
 * (O97), which stores the PUT /delivery_note/approve result in Redux `auth.approve`. One pull sheet is
 * created per warehouse; when none is left the hub opens with ?success=true (O99).
 */
export function SupplierDeliveryDistributingForm() {
  const { t } = useTranslation(["delivery", "common"])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user, approve } = useSelector((state: { auth: IAuth }) => state.auth)
  const [now] = useState(() => new Date())
  const deliveryNotes = useMemo(() => approve?.deliveryNotes ?? [], [approve?.deliveryNotes])
  const warehouses = approve?.warehouses ?? []

  const onWarehouseApproved = (warehouseId: string) => {
    const rest = warehouses.filter((w: { id?: string }) => w.id !== warehouseId)
    dispatch(authDeliveryApprove({ ...approve, warehouses: rest }))
    if (rest.length === 0) navigate("/supplier-delivery?tab=PENDING&success=true")
  }

  const breadcrumb = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/supplier-delivery", label: t("delivery:breadcrumb.deliveryManagement") },
    { label: t("delivery:breadcrumb.approveLoading") },
  ]

  return (
    <PageLayout title={t("delivery:breadcrumb.approveLoading")} subTitle={t("delivery:manage.approve.subtitle")} breadcrumb={breadcrumb}>
      {!approve || deliveryNotes.length === 0 ? (
        // O97: opened directly (no selection) — explain and send back, never an empty sheet.
        <ListState
          state="empty"
          title={t("delivery:manage.approve.noSelectionTitle")}
          description={t("delivery:manage.approve.noSelectionText")}
          action={{ label: t("delivery:manage.approve.backToPending"), onClick: () => navigate("/supplier-delivery?tab=PENDING") }}
        />
      ) : (
        <Box className={classes.sheet}>
          <Box className={classes.infoGrid}>
            <Box className={classes.infoCell}>
              {/* O98: the signed-in user confirms; the driver is the row below. */}
              <InfoCell icon={<UserIcon size={20} />} label={t("delivery:manage.approve.approver")} value={renderName(user)} />
            </Box>
            <Box className={classes.infoCell} data-divided>
              <InfoCell
                icon={<CalendarDotsIcon size={20} />}
                label={t("delivery:breadcrumb.approveLoading")}
                value={
                  <Text span ff="monospace" fw={650}>
                    {dateTimeSecFormat(now)}
                  </Text>
                }
              />
            </Box>
          </Box>

          <Group className={classes.driverStrip} justify="space-between" wrap="wrap" gap="md">
            <InfoCell dark icon={<TruckIcon size={22} />} label={t("delivery:table.deliveryStaff")} value={renderName(approve?.staff)} />
            <Group gap="xs" wrap="wrap" align="center">
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" mr={4}>
                {t("delivery:manage.approve.notes")}
              </Text>
              {deliveryNotes.map((dn: { id?: string; refCode?: string }) => (
                <Badge key={dn?.id} size="lg" radius="sm" variant="light" color="gray" ff="monospace" tt="none" c="var(--nw-text)">
                  {dn?.refCode}
                </Badge>
              ))}
            </Group>
          </Group>

          {warehouses.length === 0 ? (
            // O100: the BE answers with no warehouse when every line is already loaded or dropped.
            <ListState state="empty" title={t("delivery:manage.approve.nothingTitle")} description={t("errors:error.delivery_note.nothing_to_load")} />
          ) : (
            <SupplierDeliveryDistributingTable
              warehouses={warehouses}
              deliveryNote={deliveryNotes}
              onApproved={onWarehouseApproved}
            />
          )}
        </Box>
      )}
    </PageLayout>
  )
}
